import { useState } from 'react';
import { Badge, Button, Dropdown } from 'react-bootstrap';
import { FaClock } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { ErrorMessageHandler, standardTimeConverter } from '../../../../../_helpers/_methods';
import { fetchWrapper } from '././../../../../../_helpers/fetchWrapper';
import { AiFillCaretUp, AiFillCaretDown } from 'react-icons/ai';
import { BsImage } from 'react-icons/bs';
import AvatarStack from '../../../../../Components/AvatarStack/AvatarStack';
import { checkPermission } from '../../../../../_helpers/roles/check-roles';
import { FormattedMessage, useIntl } from 'react-intl';

// eslint-disable-next-line no-undef
const KMS_API = process.env.REACT_APP_KMS;

const StyledEventBar = styled.div`
  .event {
    min-height: 100px;
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 0.6rem;
    .eventDetail {
      width: 50%;
      h5 {
        color: #007afe;
        margin-bottom: 0.1rem;
      }
      .description {
        color: #525252;
        margin-bottom: 0.4rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .clock {
        font-size: 0.8rem;
        color: #525252;
      }
      .time {
        font-size: 0.9rem;
        color: #525252;
      }
    }
    .eventControls {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      .btn {
        width: 100%;
        padding: 0.3rem 1.2rem;
        border-radius: 1000px;
      }
      .dropdown {
        width: 100%;
      }
    }
  }
  margin-bottom: 1rem;
  background: #e8f4ff;
  border-left: 3px solid #007afe;
`;

function EventBar({ event, handleDelete, handleEdit, hasEventCompleted }) {
  const intl = useIntl();
  const activeRole = useSelector((state) => state.user.user?.active_role);
  const [showCourse, setShowCourse] = useState(false);
  const [isCompleted, setIsComplete] = useState(event?.is_completed);

  const resourcePersonStack = event?.resource_persons?.map((person) => ({
    username: person.user__fullname,
    avatar: person.user__image,
    id: person.id
  }));
  const onDeleteClick = () => {
    Swal.fire({
      title: intl.formatMessage({ id: 'are_you_sure' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes' }),
      cancelButtonText: intl.formatMessage({ id: 'cancel' }),
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(event, () => {
          Swal.fire('Deleted!', 'Session has been deleted.', 'success');
        });
      }
    });
  };

  const onEditClick = () => {
    handleEdit(event);
  };

  const handleComplete = async () => {
    try {
      await fetchWrapper.get(`/event/session/${event.id}/update-status/?action=complete/`);
      setIsComplete(true);
    } catch (err) {
      ErrorMessageHandler(err);
    }
  };
  const handleSessionRevert = async () => {
    try {
      await fetchWrapper.get(`/event/session/${event.id}/update-status/?action=incomplete/`);
      setIsComplete(false);
    } catch (err) {
      ErrorMessageHandler(err);
    }
  };

  const handleEventComplete = async () => {
    Swal.fire({
      title: intl.formatMessage({ id: 'are_you_sure' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes' }),
      cancelButtonText: intl.formatMessage({ id: 'cancel' }),
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        handleComplete(event, () => {
          Swal.fire('Completed!', 'Session has been completed.', 'success');
        });
      }
    });
  };
  const handleEventRevert = async () => {
    Swal.fire({
      title: intl.formatMessage({ id: 'are_you_sure' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes' }),
      cancelButtonText: intl.formatMessage({ id: 'cancel' }),
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        handleSessionRevert(event, () => {
          Swal.fire('Completed!', 'Session has reverted.', 'success');
        });
      }
    });
  };
  return (
    <StyledEventBar>
      <div className="event">
        <div className="eventDetail">
          <h5>{event.title}</h5>
          <p className="description">{event.description}</p>
          <div>
            <FaClock className="clock" />{' '}
            <span className="time">
              {standardTimeConverter(event.start_time)} - {standardTimeConverter(event.end_time)}
            </span>
          </div>
        </div>
        {event.course?.length ? (
          <div className="cursor-pointer" onClick={() => setShowCourse(!showCourse)}>
            <span className="me-1">
              <FormattedMessage id="courses" />
              <span className="ms-1">
                <Badge>{event.course?.length}</Badge>
              </span>{' '}
            </span>
            {showCourse ? <AiFillCaretDown /> : <AiFillCaretUp />}
          </div>
        ) : null}
        <div>
          {event?.resource_persons.length > 0 && (
            <div>
              {' '}
              <span className="me-2">
                <FormattedMessage id="event.session.with" />:
              </span>
              <AvatarStack stack={resourcePersonStack} />
            </div>
          )}
        </div>

        <div className="eventControls">
          {!hasEventCompleted && checkPermission(activeRole, 'edit session complete') ? (
            <>
              {isCompleted ? (
                <div className="d-flex">
                  <Badge bg="success" className="d-flex align-items-center me-2">
                    <FormattedMessage id="completed_session" />
                  </Badge>
                  <Button variant="outline-danger" className="btn" onClick={handleEventRevert}>
                    <FormattedMessage id="revert_session" />
                  </Button>
                </div>
              ) : (
                <Button variant="outline-primary" onClick={handleEventComplete}>
                  <FormattedMessage id="complete_session" />
                </Button>
              )}

              <Dropdown>
                <Dropdown.Toggle variant="outline-danger" id="dropdown-basic">
                  <FormattedMessage id="actions" />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item as={Button} onClick={onEditClick}>
                    <FormattedMessage id="edit_session" />
                  </Dropdown.Item>
                  <Dropdown.Item as={Button} onClick={onDeleteClick}>
                    <FormattedMessage id="delete_session" />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          ) : null}
        </div>
      </div>
      {showCourse && (
        <div className="border d-flex justify-content-center align-items-center">
          <div className="m-1">
            <h6>
              <FormattedMessage id="courses" />
            </h6>
            {event?.course?.length > 0 ? (
              event?.course?.map((course) => {
                return (
                  <div key={course} className="border border-rounded py-1 px-4 bg-white mb-1">
                    <a href={`${KMS_API}courses/${course.slug}/`} target="_blank" rel="noreferrer">
                      {course.thumbnail_image ? (
                        <img
                          src={course.thumbnail_image}
                          alt={course.title}
                          width="150"
                          height="150"
                        />
                      ) : (
                        <BsImage size="50" />
                      )}
                      <span className="ms-2">{course.title}</span>
                    </a>
                  </div>
                );
              })
            ) : (
              <span className="span">No courses</span>
            )}
          </div>
        </div>
      )}
    </StyledEventBar>
  );
}

export default EventBar;
